import { FunctionComponent } from "react";
import { Document, Page, Text, View } from "@react-pdf/renderer";
import { MAPStyles } from "src/utils/format/pdf";

interface MAPReportPDFProps {
  client: any;
  reportASummary: any;
  reportBSummary: any;
  reportCSummary: any;
  pastDue: number;
  riskReport: any;
  creditReportType: string;
}

const MAPReportPDF: FunctionComponent<MAPReportPDFProps> = ({
  client,
  reportASummary,
  reportBSummary,
  reportCSummary,
  pastDue,
  riskReport,
  creditReportType,
}) => (
  <Document>
    <Page size="A4" style={MAPStyles.page}>
      {/* --- HEADER --- */}
      <View style={MAPStyles.section}>
        <Text style={MAPStyles.header}>ClientVitals MAP Report</Text>
        <Text>{client?.userInfo?.name}</Text>
        <Text>{new Date().toLocaleDateString()}</Text>
      </View>

      {/* --- REPORT GENERATION --- */}
      <View style={MAPStyles.section}>
        <Text style={MAPStyles.subHeader}>Report Generation</Text>
        <View style={MAPStyles.row}>
          <Text style={MAPStyles.textBold}>Date & Time:</Text>
          <Text>{new Date().toLocaleString()}</Text>
        </View>
        <View style={MAPStyles.row}>
          <Text style={MAPStyles.textBold}>Pull Type:</Text>
          <Text>
            {creditReportType === "TRI_MERGE" ? "Tri-Merge" : "Single"}
          </Text>
        </View>
        <View style={MAPStyles.row}>
          <Text style={MAPStyles.textBold}>Software User:</Text>
          <Text>ClientVitals</Text>
        </View>
      </View>

      {/* --- CREDIT SCORE --- */}
      <View style={MAPStyles.section}>
        <Text style={MAPStyles.subHeader}>Credit Scores</Text>
        {[reportASummary, reportBSummary, reportCSummary].map((r, i) => (
          <View key={i} style={MAPStyles.row}>
            <Text style={MAPStyles.textBold}>{r?.repository}:</Text>
            <Text
              style={
                Number(r?.scores?.[0]?.score) < 680
                  ? MAPStyles.danger
                  : Number.isNaN(Number(r?.scores?.[0]?.score))
                  ? MAPStyles.yellow
                  : MAPStyles.success
              }
            >
              {r?.scores?.[0]?.score || "No Hit"}
            </Text>
          </View>
        ))}
      </View>

      {/* --- HARDSHIP TRACKER --- */}
      <View style={MAPStyles.section}>
        <Text style={MAPStyles.subHeader}>Hardship Tracker</Text>

        <View style={MAPStyles.gridHeader}>
          <Text style={[MAPStyles.gridCell, MAPStyles.textBold]}>
            Repository
          </Text>
          <Text style={[MAPStyles.gridCell, MAPStyles.textBold]}>Late</Text>
          <Text style={[MAPStyles.gridCell, MAPStyles.textBold]}>
            Collections
          </Text>
          <Text style={[MAPStyles.gridCell, MAPStyles.textBold]}>
            Judgments
          </Text>
          <Text style={[MAPStyles.gridCell, MAPStyles.textBold]}>
            Utilization
          </Text>
        </View>

        {[reportASummary, reportBSummary, reportCSummary].map((r, i) => (
          <View key={i} style={MAPStyles.gridHeader}>
            <Text style={MAPStyles.gridCell}>{r?.repository}</Text>
            <Text style={MAPStyles.gridCell}>
              {(Number(r?.thirtyDays) || 0) +
                (Number(r?.sixtyDays) || 0) +
                (Number(r?.ninetyDays) || 0)}
            </Text>
            <Text style={MAPStyles.gridCell}>{r?.collectionItems || 0}</Text>
            <Text style={MAPStyles.gridCell}>{r?.judgements || 0}</Text>
            <Text style={MAPStyles.gridCell}>
              {100 - (r?.availableCredit || 0)}%
            </Text>
          </View>
        ))}

        <View style={MAPStyles.gridHeader}>
          <Text style={MAPStyles.gridCell}>Total</Text>
          <Text style={MAPStyles.gridCell}>{pastDue}</Text>
        </View>
      </View>

      {/* --- TRI-MERGE DIFFERENTIALS --- */}
      <View style={MAPStyles.section}>
        <Text style={MAPStyles.subHeader}>Tri-Merge Differentials</Text>
        <View style={MAPStyles.row}>
          <Text>Amount Past Due</Text>
          <Text>90 Days Past Due</Text>
        </View>
        {[reportASummary, reportBSummary, reportCSummary].map((r, i) => (
          <View key={i} style={MAPStyles.row}>
            <Text>{r?.amountPastDue || "Unknown"}</Text>
            <Text>{r?.ninetyDays || "Unknown"}</Text>
          </View>
        ))}
      </View>

      {/* --- SERVICE RECOMMENDATIONS --- */}
      <View style={MAPStyles.section}>
        <Text style={MAPStyles.subHeader}>Service Recommendations</Text>

        {[
          ["Payment History", riskReport?.pastDueRisk?.status],
          ["Utilization", riskReport?.utilizationRisk?.status],
          ["Credit Mix", riskReport?.creditMixRisk?.status],
          ["Inquiries", riskReport?.inqueryRisk?.status],
          ["Score", riskReport?.scoreRisk?.status],
        ].map(([label, value], i) => (
          <View key={i} style={MAPStyles.row}>
            <Text style={MAPStyles.textBold}>{label} Severity:</Text>
            <Text
              style={
                value === "SEVERE"
                  ? MAPStyles.danger
                  : value === "MILD"
                  ? MAPStyles.yellow
                  : MAPStyles.success
              }
            >
              {value || ""}
            </Text>
          </View>
        ))}

        {riskReport?.overallRisk && (
          <View style={MAPStyles.box}>
            <Text style={MAPStyles.textBold}>Advised Actions</Text>
            <Text>
              {riskReport?.overallRisk === "SEVERE"
                ? "Client has Intake & Assessment Call with ClientVitals."
                : riskReport?.overallRisk === "MILD"
                ? "Client has Discovery Call with ClientVitals."
                : "No recommendations for this client."}
            </Text>
          </View>
        )}
      </View>

      {/* --- ALERTS & NOTICES --- */}
      <View style={MAPStyles.section}>
        <Text style={[MAPStyles.subHeader, { color: "#f39c12" }]}>
          Alerts & Notices
        </Text>
        {[reportASummary, reportBSummary, reportCSummary].map(
          (r, i) =>
            r?.warnings &&
            r.warnings.map((w: string, j: number) => (
              <Text key={`${i}-${j}`} style={MAPStyles.warning}>
                • {w}
              </Text>
            ))
        )}
      </View>

      <Text style={MAPStyles.footer}>
        Generated by ClientVitals • {new Date().getFullYear()}
      </Text>
    </Page>
  </Document>
);

export default MAPReportPDF;
